import { useState, lazy, Suspense } from 'react';

// better-auth's client (and zod, which it depends on) loads the moment someone
// starts to sign in, not with the page: the page itself stays inside budget.
const loadAuth = () => import('@/lib/auth-client');
import { createFileRoute, redirect, useNavigate, type SearchSchemaInput } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from '@/lib/icons';
import { safeReturnTo } from '@/lib/return-to';
import { devAutoLoginAvailable, pauseDevAutoLogin, resumeDevAutoLogin } from '@/lib/api/dev-auth';
import { devAccounts } from '@/lib/dev-accounts';
import { sessionQuery } from '@/services/session';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/surface';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

const StudioField = lazy(() => import('@/components/shell/studio-field'));
// Development only: production builds drop this import, so the demo seats never ship.
const DemoSeats = import.meta.env.PROD ? null : lazy(() => import('@/features/auth/demo-seats'));

export const Route = createFileRoute('/auth/signin')({
  // A plain validator, not zod: this route's budget is the app's entry budget.
  validateSearch: (search: { returnTo?: string; error?: string } & SearchSchemaInput): { returnTo?: string; error?: string } => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  // Already signed in? Go where they were headed.
  beforeLoad: async ({ context: { queryClient }, search }) => {
    const user = await queryClient.ensureQueryData(sessionQuery).catch(() => null);
    if (user) throw redirect({ href: safeReturnTo(search.returnTo) });
  },
  component: SignIn,
});

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden className="size-[18px]">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

function SignIn() {
  const { returnTo, error: oauthError } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'email' | 'google' | 'operator' | 'client' | null>(null);
  const [error, setError] = useState<string | null>(oauthError ? 'Google sign-in didn’t finish. Try again.' : null);
  const destination = safeReturnTo(returnTo);

  const signInWithEmail = async (as: 'email' | 'operator' | 'client', address: string, secret: string, mismatch: string) => {
    setError(null);
    setBusy(as);
    const { authClient } = await loadAuth();
    const { error: signInError } = await authClient.signIn.email({ email: address, password: secret });
    if (signInError) {
      setBusy(null);
      setError(signInError.status === 401 || signInError.status === 400 ? mismatch : signInError.message || 'Sign-in failed. Try again.');
      return;
    }
    // A person signed in: the dev team key (when this build has one) must never override their session again.
    pauseDevAutoLogin();
    // A new person may be signing in: drop everything cached for the last one,
    // then read the fresh session so the /_authed guard sees it (an
    // unobserved query is not refetched by invalidation).
    queryClient.clear();
    const user = await queryClient.fetchQuery(sessionQuery).catch(() => null);
    if (!user) {
      setBusy(null);
      setError('You’re signed in, but your browser blocked the session cookie. Allow cookies for Dreamwell and try again.');
      return;
    }
    await navigate({ href: destination, replace: true });
  };

  const onEmail = (e: React.FormEvent) => {
    e.preventDefault();
    void signInWithEmail('email', email.trim(), password, 'That email and password don’t match.');
  };

  const onGoogle = async () => {
    setError(null);
    setBusy('google');
    const { authClient, appUrl } = await loadAuth();
    // Back from Google with a session the dev team key must not override.
    pauseDevAutoLogin();
    // OAuth returns HERE, not to web-v2 (the backend's default afterSignIn).
    const { error: oauthStartError } = await authClient.signIn.social({
      provider: 'google',
      callbackURL: appUrl(destination),
      errorCallbackURL: appUrl(`/auth/signin?error=oauth&returnTo=${encodeURIComponent(destination)}`),
    });
    if (oauthStartError) {
      setBusy(null);
      setError(oauthStartError.message || 'Couldn’t start Google sign-in.');
    }
  };

  return (
    <div className="relative grid min-h-dvh place-items-center px-4 py-10">
      <Suspense fallback={null}>
        <StudioField />
      </Suspense>
      <Card
        className="w-full max-w-[400px] p-8 sm:p-10"
        onPointerEnter={() => void loadAuth()}
        onFocusCapture={() => void loadAuth()}
      >
        <div className="mb-7 flex flex-col items-center text-center">
          <img src="/brand/dreamwell-wing.png" alt="" className="mb-4 size-11" />
          <h1 className="text-[22px] font-semibold tracking-[-0.015em] text-ink">Sign in to Dreamwell</h1>
          <p className="mt-1.5 text-[14px] text-ink-3">Your creator campaigns, run by Dreamwell.</p>
        </div>

        {!import.meta.env.PROD && devAccounts().length > 0 ? (
          <div className="mb-3 flex flex-col gap-2" data-testid="dev-accounts">
            {/* Two ways to sign in on this computer: these seeded dev accounts, and the demo seats under the form. */}
            <p className="text-[12px] font-semibold text-ink-3">Dev accounts (npm run seed:dev-accounts)</p>
            <div className="grid grid-cols-2 gap-2">
              {devAccounts().map((account) => (
                <Button
                  key={account.id}
                  variant="primary"
                  size="lg"
                  disabled={busy !== null}
                  onClick={() => void signInWithEmail(account.id, account.email, account.password, 'No dev account here yet: run npm run seed:dev-accounts in backend/dreamwell-api.')}
                >
                  {busy === account.id ? <Loader2 className="animate-spin" /> : null}
                  {account.label}
                </Button>
              ))}
            </div>
            {devAutoLoginAvailable() ? (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={busy !== null}
                onClick={async () => {
                  resumeDevAutoLogin();
                  queryClient.clear();
                  await navigate({ href: destination, replace: true });
                }}
              >
                Use the dev team key (a team, not a person)
              </Button>
            ) : null}
          </div>
        ) : null}
        <Button variant="secondary" size="lg" className="w-full" onClick={onGoogle} disabled={busy !== null}>
          {busy === 'google' ? <Loader2 className="animate-spin" /> : <GoogleMark />}
          Continue with Google
        </Button>

        <div className="my-6 flex items-center gap-3 text-[12px] text-ink-4">
          <span className="h-px flex-1 bg-line" />
          or with email
          <span className="h-px flex-1 bg-line" />
        </div>

        <form onSubmit={onEmail} className="flex flex-col gap-3" noValidate>
          <Field label="Email">
            <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </Field>
          <Field label="Password">
            <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          {error ? (
            <p role="alert" className="rounded-[12px] bg-rose-bg px-3 py-2 text-[13px] text-rose-ink">
              {error}
            </p>
          ) : null}
          <Button type="submit" variant="primary" size="lg" className="mt-2 w-full" disabled={busy !== null || !email || !password}>
            {busy === 'email' ? <Loader2 className="animate-spin" /> : null}
            Sign in
          </Button>
        </form>

        {DemoSeats ? (
          <Suspense fallback={null}>
            <DemoSeats
              busy={busy !== null}
              onPick={(seat) => {
                setEmail(seat.email);
                setPassword(seat.password);
                void signInWithEmail('email', seat.email, seat.password, 'That demo seat’s email and password don’t match in this database.');
              }}
            />
          </Suspense>
        ) : null}
      </Card>
    </div>
  );
}
